import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useStore } from "@/model/store";

/**
 * The active scenario is client state (API plan §8.12), kept in the URL as `?scenario=<slug>` so a reload,
 * a shared link and the back button keep it. The store stays the source for in-app changes; the URL is
 * the source when the page loads and when the browser moves through history.
 */
export function useScenarioInUrl() {
	const location = useLocation();
	const navType = useNavigationType();
	const navigate = useNavigate();
	const active = useStore((s) => s.activeScenario);
	const scenarios = useStore((s) => s.world.scenarios);
	const loaded = useRef(false);

	// On load and on back/forward: the URL says which scenario to show.
	useEffect(() => {
		if (loaded.current && navType !== "POP") return;
		loaded.current = true;
		const ref = new URLSearchParams(location.search).get("scenario");
		const sc = ref ? scenarios.find((s) => s.slug === ref || s.id === ref) : null;
		if ((sc?.id ?? null) !== useStore.getState().activeScenario) useStore.getState().setActiveScenario(sc?.id ?? null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.key]);

	// After that, the URL follows the store (replacing, so it adds no history entries).
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		// Read the store, not `active`: the effect above may have just set it from the URL in this same commit.
		const current = useStore.getState().activeScenario;
		const want = current ? (scenarios.find((s) => s.id === current)?.slug ?? null) : null;
		if ((params.get("scenario") ?? null) === want) return;
		if (want) params.set("scenario", want);
		else params.delete("scenario");
		const search = params.toString();
		navigate({ pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash }, { replace: true });
	}, [active, scenarios, location.pathname, location.search, location.hash, navigate]);
}
